import React, { ReactNode } from "react";
import styled from "styled-components";
import * as Dialog from "@radix-ui/react-dialog";
import {
  Theme,
  media,
  LinkNext,
  Image,
} from "@literati/public-ui-shared-react";
import { LINKS } from "globalConfig";
import {
  DialogClose,
  DialogContent,
  DialogOverlay,
  centerDesktopContentStyles,
} from "components/Dialog/DialogPrimitives";

const CLASS_BASE = "book-checkout-credit-dialog";

export const BookCheckoutCreditDialogClassName = {
  BASE: CLASS_BASE,
  OVERLAY: `${CLASS_BASE}-overlay`,
  CONTENT: `${CLASS_BASE}-content`,
} as const;

export interface BookCheckoutCreditDialogProps {
  maxPerInvoice?: string | null;
  label: string;
  trigger: ReactNode;
  linkToAccount?: boolean;
}

const StyledDialogContent = styled(DialogContent)`
  ${media.sm} {
    ${centerDesktopContentStyles}
    width: 100%;
  }

  ${media.md} {
    width: 600px;
    max-width: unset;
  }
`;

const StyledBookCheckoutAdditionalInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1.5rem;
  text-align: center;
`;

const StyledTitle = styled.h3`
  margin-top: 20px;
  margin-bottom: 10px;
  font-weight: 600;
`;

export const BookCheckoutCreditDialog = ({
  maxPerInvoice,
  label,
  trigger,
  linkToAccount = false,
}: BookCheckoutCreditDialogProps) => {
  if (!maxPerInvoice) {
    return null;
  }

  const cls = BookCheckoutCreditDialogClassName;
  return (
    <Theme>
      <Dialog.Root modal>
        <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
        <Dialog.Portal>
          <DialogOverlay className={cls.OVERLAY}></DialogOverlay>
          <StyledDialogContent className={cls.CONTENT}>
            <DialogClose />
            <StyledBookCheckoutAdditionalInfo>
              <Image
                alt=""
                src="https://siren-images.literati.com/documents/book_money.svg"
                height={100}
                width={100}
              />
              <StyledTitle>How This Credit Works</StyledTitle>
              <p>
                With the existing credit from <strong>{label}</strong>, you will
                be able to apply <strong>up to {maxPerInvoice}</strong> towards
                each purchase. Unspent credit can still be used toward future
                purchases, so no worries if you don’t spend it all!
                {linkToAccount && (
                  <>
                    {" Remaining credit can be found in "}
                    <LinkNext href={LINKS.account} legacyBehavior={false}>
                      Account Details
                    </LinkNext>
                    .
                  </>
                )}
              </p>
            </StyledBookCheckoutAdditionalInfo>
          </StyledDialogContent>
        </Dialog.Portal>
      </Dialog.Root>
    </Theme>
  );
};

export default BookCheckoutCreditDialog;
